import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const STATUS = {
  EN_COURS:  ['navy',   'En cours'],
  RETOURNEE: ['green',  'Retourné'],
  EN_RETARD: ['red',    'En retard'],
  PERDU:     ['gray',   'Perdu'],
};

const BORROWER_TYPES = ['Élève', 'Enseignant', 'Personnel'];

export default function LibraryEmprunts() {
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('');

  function load() {
    api.get('/library/loans').then((data) => setLoans(data || [])).catch((e) => setError(e.message));
  }

  useEffect(load, []);

  async function returnLoan(id) {
    if (!(await confirmDialog({ title: 'Retour d\'ouvrage', message: 'Enregistrer le retour de cet ouvrage ?', confirmLabel: 'Confirmer le retour' }))) return;
    try { await api.post(`/library/loans/${id}/return`); load(); } catch (e) { setError(e.message); }
  }

  const filtered = loans.filter((l) => !filter || (l.status || 'EN_COURS') === filter);

  const stats = {
    en_cours: loans.filter((l) => (l.status || 'EN_COURS') === 'EN_COURS').length,
    retournes: loans.filter((l) => l.status === 'RETOURNEE').length,
    en_retard: loans.filter((l) => l.status === 'EN_RETARD').length,
  };

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi navy"><span>En cours</span><strong>{stats.en_cours}</strong></div>
        <div className="kpi green"><span>Retournés</span><strong>{stats.retournes}</strong></div>
        <div className="kpi"><span>En retard</span><strong>{stats.en_retard}</strong></div>
        <div className="kpi"><span>Total</span><strong>{loans.length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Gestion des emprunts</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 150 }}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS).map(([v, [, l]]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({})}>+ Nouvel emprunt</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun emprunt.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Emprunteur</th><th>Type</th><th>Ouvrage</th><th>Code</th>
                  <th>Date emprunt</th><th>Retour prévu</th><th>Durée</th><th>Statut</th><th></th>
                </tr>
              </thead>
              <tbody>{filtered.map((l) => {
                const statusKey = l.status || 'EN_COURS';
                const [bc, bl] = STATUS[statusKey] || ['gray', statusKey];
                const dueDate = l.dueDate || l.due_date;
                const isLate = statusKey === 'EN_COURS' && dueDate && new Date(dueDate) < new Date();
                return (
                  <tr key={l.id} style={isLate ? { background: 'var(--danger-soft, #fff5f5)' } : {}}>
                    <td>
                      <b>{l.lastName || l.last_name} {l.firstName || l.first_name}</b>
                      <br /><small className="mono">{l.matricule}</small>
                    </td>
                    <td>{l.borrowerType || 'Élève'}</td>
                    <td><b>{l.title}</b><br /><small style={{ color: 'var(--muted)' }}>{l.author}</small></td>
                    <td className="mono">{l.code}</td>
                    <td className="mono">{l.loanDate || l.loan_date ? new Date(l.loanDate || l.loan_date).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="mono" style={isLate ? { color: 'var(--danger)', fontWeight: 700 } : {}}>
                      {dueDate ? new Date(dueDate).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="num">{l.days_overdue ? <span style={{ color: 'var(--danger)' }}>+{l.days_overdue}j</span> : '—'}</td>
                    <td><span className={`badge ${bc}`}>{bl}</span></td>
                    <td>
                      {statusKey === 'EN_COURS' && (
                        <button className="btn-ghost btn-sm" onClick={() => returnLoan(l.id)}>Retour</button>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>
      {modal && <EmpruntModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}

function EmpruntModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ studentId: '', copyId: '', borrowerType: 'Élève' });
  const [students, setStudents] = useState([]);
  const [copies, setCopies] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/students').then(setStudents).catch(() => {});
    api.get('/library/copies').then(setCopies).catch(() => {});
  }, []);

  async function save() {
    if (!form.studentId || !form.copyId) { setError('Tous les champs sont requis.'); return; }
    setSaving(true);
    try { await api.post('/library/loans', form); onSaved(); }
    catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvel emprunt</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Type d'emprunteur</label>
          <select value={form.borrowerType} onChange={set('borrowerType')}>
            {BORROWER_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field"><label>Élève / Emprunteur</label>
          <select value={form.studentId} onChange={set('studentId')}>
            <option value="">Sélectionner…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName} ({s.matricule})</option>)}
          </select>
        </div>
        <div className="field"><label>Exemplaire disponible</label>
          <select value={form.copyId} onChange={set('copyId')}>
            <option value="">Sélectionner…</option>
            {copies.map((c) => <option key={c.id} value={c.id}>{c.title} — {c.code}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}
